import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker, Form, Input, TimePicker, Upload, message } from "antd";
import dayjs from "dayjs";
import FormItem from "components/fields/FormItem";
import CrudButtons from "components/button/crudbuttons";
import { TIME_FORMAT_12HM } from "variables/constants";
import {
	getTimecardUpdateRequestDetails,
	doTimecardUpdateRequestCreate,
	doTimecardUpdateRequestUpdate,
	resetTimecardUpdateRequestState
} from "../../../../../redux/timecardupdaterequest/timecardUpdateRequestState";
import { requiredField } from "utils/crudSagaHelper";
import { CalendarRange } from "lucide-react";
import { AiOutlineSwapRight } from "react-icons/ai";
import APIService from '../../../../../services/apiservice';
import { showLoading } from '../../../../../redux/layout/layoutState';

const TimecardUpdateRequestForm = ({
	resetState,
	mode = "edit",
	onCancel,
	timecard
}) => {

	const dispatch = useDispatch();
	const createSuccess = useSelector(
		(state) => state.timecardUpdateRequest.timecardUpdateRequestDetails.success
	);

	const [form] = Form.useForm();
	const { RangePicker } = DatePicker;

	const updatedSchedule = Form.useWatch("updated_schedule", form);
	const updatedPunchIn = Form.useWatch("updated_punch_in", form);
	const updatedPunchOut = Form.useWatch("updated_punch_out", form);
	const changeValidator = {
		validator: () => {
			if (hasChanges) {
				return Promise.resolve();
			}

			return Promise.reject(
				new Error("Please make at least one change")
			);
		},
	};

	const hasChanges = React.useMemo(() => {
		const originalScheduleIn = timecard?.tmc_schedule_time_in
			? dayjs(timecard.tmc_schedule_time_in).format("HH:mm:ss")
			: null;

		const originalScheduleOut = timecard?.tmc_schedule_time_out
			? dayjs(timecard.tmc_schedule_time_out).format("HH:mm:ss")
			: null;

		const originalPunchIn = timecard?.tmc_punch_in
			? dayjs(timecard.tmc_punch_in).format("HH:mm:ss")
			: null;

		const originalPunchOut = timecard?.tmc_punch_out
			? dayjs(timecard.tmc_punch_out).format("HH:mm:ss")
			: null;

		const scheduleChanged =
			(updatedSchedule?.[0]?.format("HH:mm:ss") ?? null) !== originalScheduleIn ||
			(updatedSchedule?.[1]?.format("HH:mm:ss") ?? null) !== originalScheduleOut;

		const punchChanged =
			(updatedPunchIn?.format("HH:mm:ss") ?? null) !== originalPunchIn ||
			(updatedPunchOut?.format("HH:mm:ss") ?? null) !== originalPunchOut;

		return scheduleChanged || punchChanged;
	}, [updatedSchedule, updatedPunchIn,updatedPunchOut, timecard]);
	
	useEffect(() => {
		if (createSuccess) {
			dispatch(resetTimecardUpdateRequestState());
			onCancel?.();
		}
	}, [createSuccess, onCancel, resetState]);

	useEffect(() => {
		if (!timecard) return;

		form.resetFields();

		form.setFieldsValue({
			tur_timecard_id: timecard.tmc_id,
			current_schedule: timecard.tmc_schedule_time_in
				? `${dayjs(timecard.tmc_schedule_time_in).format(TIME_FORMAT_12HM)} - ${dayjs(timecard.tmc_schedule_time_out).format(TIME_FORMAT_12HM)}`
				: "Rest Day",
			current_punch: `${timecard.tmc_punch_in
				? dayjs(timecard.tmc_punch_in).format(TIME_FORMAT_12HM)
				: "-"} - ${
				timecard.tmc_punch_out
					? dayjs(timecard.tmc_punch_out).format(TIME_FORMAT_12HM)
					: "-"
			}`,
			updated_schedule: timecard.tmc_schedule_time_in
				? [ dayjs(timecard.tmc_schedule_time_in),
					dayjs(timecard.tmc_schedule_time_out),]
				: null,

			updated_punch_in: timecard.tmc_punch_in
				? dayjs(timecard.tmc_punch_in)
				: null,
			updated_punch_out: timecard.tmc_punch_out
				? dayjs(timecard.tmc_punch_out)
				: null,
		});
	}, [timecard, form]);

	const [attachmentFile, setAttachmentFile] = useState();

	const handleUpload = async (file) => {
		const isValidType =
		file.type === 'image/jpeg' ||
		file.type === 'image/png' ||
		file.type === 'image/jpg';

		if (!isValidType) {
		message.error('Only JPG, JPEG, PNG files are allowed');
		return Upload.LIST_IGNORE;
		}

		const isLessThan5MB = file.size / 1024 / 1024 < 2;

		if (!isLessThan5MB) {
		message.error('File must be smaller than 2MB');
		return Upload.LIST_IGNORE;
		}

		setAttachmentFile(file);
		return false; 
	}

	const onSubmit = async (formData) => {

		try {

			const { file, ...turData } = formData;

			const date = dayjs(timecard.tmc_date).format("YYYY-MM-DD");

			dispatch(showLoading('Uploading...'));
			const result = await APIService.doUploadRequest(`api/v1/console/upload/attachment`, attachmentFile);
			const attachment = result.data;

			turData.tur_attachment_id = attachment?.att_id ?? 0;

			turData.tur_requested_schedule_time_in =
				turData.updated_schedule?.[0]
					? `${date} ${turData.updated_schedule[0].format("HH:mm:ss")}`
					: null;

			turData.tur_requested_schedule_time_out =
				turData.updated_schedule?.[1]
					? `${date} ${turData.updated_schedule[1].format("HH:mm:ss")}`
					: null;
					
			turData.tur_requested_punch_in =
				turData.updated_punch_in
					? `${date} ${turData.updated_punch_in.format("HH:mm:ss")}`
					: null;

			turData.tur_requested_punch_out =
				turData.updated_punch_out
					? `${date} ${turData.updated_punch_out.format("HH:mm:ss")}`
					: null;

			delete turData.updated_schedule;
			delete turData.updated_punch;

			dispatch(doTimecardUpdateRequestCreate(turData));

		} catch (error) {
			console.error('Save failed:', error);
		}
	};

	return (
		<Form
			form={form}
			layout="vertical"
			className="p-4 bg-white rounded-md dark:bg-intsamis-900"
			onFinish={onSubmit}
		>
			<FormItem name="tur_id" hidden>
				<Input />
			</FormItem>

			<FormItem name="tur_timecard_id" hidden>
				<Input />
			</FormItem>

			<div className="grid grid-cols-2 gap-4">
				<FormItem label="Schedule" name="current_schedule">
					<Input readOnly className="input-field" />
				</FormItem>

				<FormItem label="Time In / Time Out" name="current_punch">
					<Input readOnly className="input-field" />
				</FormItem>

				<FormItem 
					label="Update Schedule" 
					name="updated_schedule"
					dependencies={["updated_punch_in", "updated_punch_out"]}
					rules={[changeValidator]}
				>
					<RangePicker
						picker="time"
						format={TIME_FORMAT_12HM}
						className="input-field"
						style={{ width: "100%" }}
						allowClear={false}
					/>
				</FormItem>
				
				<FormItem
					label="Update Time In / Time Out"
					name="change_validator"
					dependencies={[
						"updated_schedule",
						"updated_punch_in",
						"updated_punch_out",
					]}
					rules={[changeValidator]}
					
				>
					<div className="flex gap-2 items-center">
						<FormItem
								name="updated_punch_in"
								noStyle
							>
								<TimePicker
									format={TIME_FORMAT_12HM}
									className="input-field"
								/>
							</FormItem>
							<span className="text-base text-gray-500"><AiOutlineSwapRight/></span>
							<FormItem
								name="updated_punch_out"
								noStyle
							>
								<TimePicker
									format={TIME_FORMAT_12HM}
									className="input-field"
								/>
						</FormItem>
					</div>
				</FormItem>
			</div>

			<FormItem 
				label="Reason" 
				name="tur_reason"
				rules={[requiredField]}
			>
				<Input.TextArea
					rows={4}
					placeholder="Enter reason for update request"
				/>
			</FormItem>
			<div className="mb-4 w-full">
				<FormItem
					name="file"
					iconName="UploadIcon"
					label="Upload Screenshot"
					valuePropName="fileList"
					getValueFromEvent={(e) => {
					if (Array.isArray(e)) return e;
					return e?.fileList;
					}}
					rules={[
					{
					required: true,
					message: 'Please upload a screenshot',
					},
					]}
				>
					<Upload.Dragger
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						beforeUpload={handleUpload}
					>
						<div className="flex flex-col w-full items-center gap-2">
							<div className="btn-gradient-emerald px-3 py-1.5 rounded-lg w-[100px]">
								Choose File
							</div>
							<div>
								<p className="text-sm text-left">
								Accepted formats: JPG, JPEG, PNG (Max 2MB)
								</p>
							</div>
						</div>
						<div className="w-full p-6">
						<p className="text-[18px] text-center text-gray-500">
							Click or drag file to this area to upload
						</p>
						</div>
					</Upload.Dragger>
				</FormItem>
			</div>

			<CrudButtons
				submitDisabled={!hasChanges}
				isModal
				cancelLabel="Cancel"
				doHideModal={onCancel}
				submitLabel="Submit"
				moduleId={0}
			/>
		</Form>
	);
};


export default (TimecardUpdateRequestForm);